/**
 * LacedUpChicago — Product Page
 *
 * Enhances the server-rendered product page. Everything it does is an
 * enhancement over markup that already works: with this script absent
 * or broken, the native GET selection form changes variants and the
 * native Shopify product form purchases the exact server-selected
 * variant — and only when that variant is genuinely available.
 *
 * NOT here, by specification: AJAX add-to-cart, Cart Drawer, Header
 * cart-count updates, recommendations, sticky add-to-cart, zoom, and
 * any inventory/scarcity messaging.
 *
 * VARIANT ARCHITECTURE
 * Selection is driven by `product_option_value` IDs plus Shopify's
 * Section Rendering API — never by an embedded `product.variants | json`
 * map, which silently truncates above Shopify's Liquid variant cap.
 *
 * THREE PURCHASE STATES
 *   ready   server-rendered state applies; purchase allowed only if the
 *           exact selected variant is available
 *   pending a variant request is in flight; the picker already shows a
 *           new selection while the form still holds the previous
 *           variant, so EVERY purchase path is closed (hidden ID
 *           disabled, button disabled, wallet inert, quantity frozen,
 *           submit prevented) and aria-busy is set
 *   locked  a request failed or a fragment transaction aborted; busy
 *           semantics are cleared but purchase stays closed until a
 *           later option change succeeds
 *
 * Pending and locked are distinct so the page is never left permanently
 * marked busy after a failure.
 *
 * TRANSACTIONAL UPDATES
 * Every required fragment must be present in both the response and the
 * current DOM before any DOM is touched, and the whole apply runs in a
 * try/catch. If any stage throws — including PaymentButton
 * initialization — the page falls into the locked state rather than
 * surviving as a partially updated purchase form. The AbortController
 * identity is not released until the transaction has fully succeeded.
 */
(function () {
  'use strict';

  var ENHANCED_ATTR = 'data-product-enhanced';
  var PENDING_CLASS = 'is-pending';
  var LOCKED_CLASS = 'is-locked';

  // SKU is intentionally optional: the block may not be present at all.
  var REQUIRED_FRAGMENTS = [
    '[data-product-variant-picker]',
    '[data-product-form-container]',
    '[data-product-price]',
    '[data-product-availability]'
  ];
  var OPTIONAL_FRAGMENTS = ['[data-product-sku]'];

  function isConnected(el) {
    return !!el && document.body.contains(el);
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /** One controller per section, so stale responses are discarded. */
  var controllers = new WeakMap();

  function abortPending(section) {
    var pending = controllers.get(section);
    if (pending) {
      pending.abort();
      controllers.delete(section);
    }
  }

  function getOptionInputs(section) {
    return Array.prototype.slice.call(
      section.querySelectorAll('[data-option-value-id]')
    );
  }

  /**
   * Collects the checked option-value IDs in product-option order,
   * which is the order Shopify's `option_values` parameter expects.
   */
  function collectOptionValueIds(section) {
    var checked = getOptionInputs(section).filter(function (input) {
      return input.checked;
    });

    checked.sort(function (a, b) {
      return (
        parseInt(a.dataset.optionPosition, 10) -
        parseInt(b.dataset.optionPosition, 10)
      );
    });

    return checked.map(function (input) {
      return input.dataset.optionValueId;
    });
  }

  function setError(section, message) {
    var errorEl = section.querySelector('[data-product-error]');
    if (!errorEl) return;

    if (message) {
      errorEl.textContent = message;
      errorEl.removeAttribute('hidden');
    } else {
      errorEl.textContent = '';
      errorEl.setAttribute('hidden', '');
    }
  }

  /* ==================================================================
     Purchase state
     ================================================================== */

  function parts(section) {
    return {
      container: section.querySelector('[data-product-form-container]'),
      hiddenId: section.querySelector('[data-product-variant-id]'),
      submit: section.querySelector('[data-product-submit]'),
      wallet: section.querySelector('[data-product-dynamic-checkout]'),
      quantity: section.querySelector('[data-product-quantity]')
    };
  }

  /** Closes every purchase path. Shared by pending and locked. */
  function closePurchase(section, freezeQuantity) {
    var p = parts(section);
    if (p.hiddenId) p.hiddenId.disabled = true; // value preserved
    if (p.submit) p.submit.disabled = true;
    if (p.quantity) p.quantity.disabled = !!freezeQuantity;
    if (p.wallet) {
      p.wallet.hidden = true;
      p.wallet.setAttribute('aria-hidden', 'true');
    }
  }

  function setPending(section) {
    var p = parts(section);
    closePurchase(section, true);
    if (p.container) {
      p.container.classList.add(PENDING_CLASS);
      p.container.classList.remove(LOCKED_CLASS);
      p.container.setAttribute('aria-busy', 'true');
    }
  }

  /**
   * Post-failure resting state: no longer busy, but still not
   * purchasable. Quantity is re-enabled because nothing is in flight.
   */
  function setLocked(section, message) {
    var p = parts(section);
    closePurchase(section, false);
    if (p.container) {
      p.container.classList.remove(PENDING_CLASS);
      p.container.classList.add(LOCKED_CLASS);
      p.container.removeAttribute('aria-busy');
    }
    setError(section, message || section.dataset.errorMessage || '');
  }

  /**
   * Applies the server-rendered truth. The purchase form is opened only
   * when the exact selected variant is available.
   */
  function applyEnhancedFormState(section) {
    var p = parts(section);
    if (!p.container) return;

    var hasVariant = p.container.dataset.hasVariant === 'true';
    var isAvailable = p.container.dataset.variantAvailable === 'true';

    p.container.classList.remove(PENDING_CLASS, LOCKED_CLASS);
    p.container.removeAttribute('aria-busy');

    if (p.hiddenId) p.hiddenId.disabled = !(isAvailable && p.hiddenId.value);
    if (p.quantity) p.quantity.disabled = false;
    if (p.wallet) {
      p.wallet.hidden = !isAvailable;
      p.wallet.setAttribute('aria-hidden', isAvailable ? 'false' : 'true');
    }

    if (p.submit) {
      p.submit.disabled = !isAvailable;
      if (isAvailable) {
        p.submit.textContent = p.submit.dataset.labelAdd || p.submit.textContent;
      } else if (hasVariant) {
        p.submit.textContent = p.submit.dataset.labelSoldOut || p.submit.textContent;
      } else {
        p.submit.textContent = p.submit.dataset.labelUnavailable || p.submit.textContent;
      }
    }

    // The GET selection form is the no-JS path; retire it entirely once
    // the enhanced picker is driving selection.
    var selectionForm = section.querySelector('[data-product-selection-form]');
    if (selectionForm) {
      Array.prototype.forEach.call(
        selectionForm.querySelectorAll('select, button'),
        function (control) {
          control.disabled = true;
        }
      );
    }
  }

  /**
   * The single authority on whether a submission may proceed. Used by
   * the submit handler so implicit (Enter), programmatic
   * (requestSubmit) and clicked submissions all pass the same gate.
   */
  function canSubmit(section) {
    var p = parts(section);
    if (!p.container) return false;
    if (p.container.classList.contains(PENDING_CLASS)) return false;
    if (p.container.classList.contains(LOCKED_CLASS)) return false;
    if (p.container.dataset.variantAvailable !== 'true') return false;
    if (!p.submit || p.submit.disabled) return false;
    if (!p.hiddenId || p.hiddenId.disabled || !p.hiddenId.value) return false;
    return true;
  }

  /**
   * Shopify renders wallet buttons once at load; a replaced container
   * holds inert markup still bound to the superseded variant. Allowed
   * to throw — the caller's transaction catch handles it.
   */
  function reinitPaymentButton(section) {
    var wallet = section.querySelector('[data-product-dynamic-checkout]');
    if (!wallet || !isConnected(wallet)) return;

    if (
      window.Shopify &&
      window.Shopify.PaymentButton &&
      typeof window.Shopify.PaymentButton.init === 'function'
    ) {
      window.Shopify.PaymentButton.init();
    }
  }

  /* ==================================================================
     Media
     ================================================================== */

  function activateMedia(section, mediaId, options) {
    if (!mediaId) return;
    options = options || {};

    var items = section.querySelectorAll('[data-product-media-item]');
    Array.prototype.forEach.call(items, function (item) {
      var isTarget = item.dataset.mediaId === String(mediaId);
      var wasActive = item.classList.contains('is-active');
      item.classList.toggle('is-active', isTarget);

      if (!isTarget && wasActive) {
        var playing = item.querySelector('video');
        if (playing && typeof playing.pause === 'function') playing.pause();

        // Reloading the provider iframe is the only reliable way to
        // stop a third-party player without embedding its SDK. Done
        // only on the item actually leaving the stage.
        var frame = item.querySelector('iframe');
        if (frame && frame.src) frame.src = frame.src;
      }
    });

    var thumbs = section.querySelectorAll('[data-product-gallery-thumb]');
    Array.prototype.forEach.call(thumbs, function (thumb) {
      var isTarget = thumb.dataset.mediaId === String(mediaId);
      thumb.classList.toggle('is-active', isTarget);
      if (isTarget) {
        thumb.setAttribute('aria-current', 'true');
      } else {
        thumb.removeAttribute('aria-current');
      }
    });

    var gallery = section.querySelector('[data-product-gallery]');
    if (gallery) gallery.dataset.productSelectedMedia = mediaId;

    if (options.scroll === false) return;
    var active = section.querySelector(
      '[data-product-media-item][data-media-id="' + mediaId + '"]'
    );
    var track = section.querySelector('[data-product-gallery-track]');
    if (active && track && track.scrollWidth > track.clientWidth) {
      track.scrollTo({
        left: active.offsetLeft - track.offsetLeft,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });
    }
  }

  /**
   * Keeps active state honest when the customer swipes instead of using
   * a thumbnail. One rAF-throttled listener on the track.
   */
  function bindTrackScroll(section) {
    var track = section.querySelector('[data-product-gallery-track]');
    if (!track) return;

    var ticking = false;
    track.addEventListener(
      'scroll',
      function () {
        if (ticking) return;
        ticking = true;

        window.requestAnimationFrame(function () {
          ticking = false;

          var items = track.querySelectorAll('[data-product-media-item]');
          if (!items.length) return;

          var center = track.scrollLeft + track.clientWidth / 2;
          var nearest = null;
          var nearestDistance = Infinity;

          Array.prototype.forEach.call(items, function (item) {
            var itemCenter = item.offsetLeft - track.offsetLeft + item.clientWidth / 2;
            var distance = Math.abs(itemCenter - center);
            if (distance < nearestDistance) {
              nearestDistance = distance;
              nearest = item;
            }
          });

          if (nearest && !nearest.classList.contains('is-active')) {
            activateMedia(section, nearest.dataset.mediaId, { scroll: false });
          }
        });
      },
      { passive: true }
    );
  }

  /* ==================================================================
     Variant updates
     ================================================================== */

  function updateUrl(section) {
    var container = section.querySelector('[data-product-form-container]');
    var idInput = section.querySelector('[data-product-variant-id]');
    var hasVariant = container && container.dataset.hasVariant === 'true';
    var url = new URL(window.location.href);

    if (hasVariant && idInput && idInput.value) {
      url.searchParams.set('variant', idInput.value);
      url.searchParams.delete('option_values');
    } else {
      url.searchParams.delete('variant');
      var ids = collectOptionValueIds(section);
      if (ids.length) {
        url.searchParams.set('option_values', ids.join(','));
      } else {
        url.searchParams.delete('option_values');
      }
    }

    window.history.replaceState({}, '', url.toString());
  }

  function readQuantity(section) {
    var input = section.querySelector('[data-product-quantity]');
    if (!input) return null;
    var value = parseInt(input.value, 10);
    return isNaN(value) || value < 1 ? null : String(value);
  }

  function restoreQuantity(section, value) {
    if (!value) return;
    var input = section.querySelector('[data-product-quantity]');
    if (input) input.value = value;
  }

  function announceState(section) {
    var region = section.querySelector('[data-product-live-region]');
    if (!region) return;

    var price = section.querySelector('[data-product-price]');
    var availability = section.querySelector('[data-product-availability]');

    var text = [];
    if (price) text.push(price.textContent.trim().replace(/\s+/g, ' '));
    if (availability) text.push(availability.textContent.trim().replace(/\s+/g, ' '));

    region.textContent = text.join('. ');
  }

  /**
   * Every required fragment must exist on BOTH sides before any DOM is
   * touched, so a malformed response can never produce a half-updated
   * purchase state.
   */
  function transactionReady(section, doc) {
    return REQUIRED_FRAGMENTS.every(function (selector) {
      return !!doc.querySelector(selector) && !!section.querySelector(selector);
    });
  }

  function applyUpdate(section, doc, focusedOptionValueId, quantityValue) {
    REQUIRED_FRAGMENTS.concat(OPTIONAL_FRAGMENTS).forEach(function (selector) {
      var incoming = doc.querySelector(selector);
      var current = section.querySelector(selector);
      if (incoming && current) current.replaceWith(incoming);
    });

    applyEnhancedFormState(section);
    restoreQuantity(section, quantityValue);
    reinitPaymentButton(section);

    if (focusedOptionValueId) {
      var restored = section.querySelector(
        '[data-option-value-id="' + focusedOptionValueId + '"]'
      );
      if (restored) restored.focus();
    }

    var incomingGallery = doc.querySelector('[data-product-gallery]');
    if (incomingGallery) {
      var nextMedia = incomingGallery.getAttribute('data-product-selected-media');
      if (nextMedia) activateMedia(section, nextMedia);
    }

    announceState(section);
    updateUrl(section);
  }

  function fetchVariant(section, focusedOptionValueId) {
    var sectionId = section.dataset.sectionId;
    var productUrl = section.dataset.productUrl;
    var optionValueIds = collectOptionValueIds(section);
    if (!sectionId || !productUrl) return;

    abortPending(section);
    var controller = new AbortController();
    controllers.set(section, controller);

    // Read the quantity BEFORE freezing it, then close every purchase
    // path for the duration of the request.
    var quantityValue = readQuantity(section);
    setError(section, null);
    setPending(section);

    var url = new URL(productUrl, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('option_values', optionValueIds.join(','));

    fetch(url.toString(), { signal: controller.signal })
      .then(function (response) {
        if (!response.ok) throw new Error('Section request failed');
        return response.text();
      })
      .then(function (html) {
        if (controllers.get(section) !== controller) return;
        if (!isConnected(section)) return;

        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (!transactionReady(section, doc)) {
          throw new Error('Missing required fragment');
        }

        try {
          applyUpdate(section, doc, focusedOptionValueId, quantityValue);
        } catch (applyError) {
          // A throw mid-apply (including PaymentButton init) must not
          // leave a partially updated, purchasable form.
          setLocked(section);
          controllers.delete(section);
          return;
        }

        // Identity released only after the transaction fully succeeded.
        controllers.delete(section);
      })
      .catch(function (error) {
        if (error && error.name === 'AbortError') return;
        // A stale failure must not clear or report over a newer request.
        if (controllers.get(section) !== controller) return;
        if (!isConnected(section)) return;

        controllers.delete(section);
        setLocked(section);
      });
  }

  /* ==================================================================
     Init
     ================================================================== */

  function initProduct(section) {
    if (section.dataset.productInitialized === 'true') return;
    section.dataset.productInitialized = 'true';

    section.setAttribute(ENHANCED_ATTR, '');
    applyEnhancedFormState(section);
    bindTrackScroll(section);

    section.addEventListener('change', function (event) {
      var input = event.target.closest('[data-option-value-id]');
      if (!input || !section.contains(input)) return;

      // A combined-listing value pointing at another product is a
      // navigation, not an in-place update.
      var productUrl = input.dataset.productUrl;
      if (productUrl) {
        window.location.href = productUrl;
        return;
      }

      fetchVariant(section, input.dataset.optionValueId);
    });

    section.addEventListener('click', function (event) {
      var thumb = event.target.closest('[data-product-gallery-thumb]');
      if (thumb && section.contains(thumb)) {
        activateMedia(section, thumb.dataset.mediaId);
      }
    });

    // Single gate for click, Enter and requestSubmit alike.
    section.addEventListener('submit', function (event) {
      var form = event.target.closest('[data-product-form-container] form');
      if (!form) return;

      if (!canSubmit(section)) {
        event.preventDefault();
        return;
      }

      var submit = section.querySelector('[data-product-submit]');
      if (submit) submit.classList.add('button--loading');
    });
  }

  var documentListenersBound = false;

  function bindDocumentListeners() {
    if (documentListenersBound) return;
    documentListenersBound = true;

    document.addEventListener('shopify:section:unload', function (event) {
      var section = event.target.querySelector
        ? event.target.querySelector('[data-product-section]')
        : null;
      if (section) abortPending(section);
    });
  }

  window.theme = window.theme || {};
  if (window.theme.onSectionLoad) {
    window.theme.onSectionLoad('[data-product-section]', initProduct);
  }
  bindDocumentListeners();
})();
